import * as stylex from '@stylexjs/stylex'
import { type ElementType } from 'react'
import { Box, type BoxProps } from '../Box/Box'
import { colors } from '../tokens/colors.stylex'
import { system } from '../tokens/system.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const _baseStyles = stylex.create({
  /**
   * Reset default hr tag style
   */
  reset: {
    margin: 0,
    borderColor: 'transparent',
    borderStyle: 'none',
    borderWidth: 0,
    flexShrink: 0,
  },
  root: {
    borderColor: 'currentColor',
    borderStyle: 'solid',
    color: colors.grey300,
    /**
     * Fixed for flatsome.css
     * https://beta.dogcatstar.com/wp-content/themes/flatsome/assets/css/flatsome.css?ver=3.14.3
     */
    opacity: 1,
    borderTopWidth: 'thin',
    width: '100%',
  },
  dividerWithChildren: {
    borderStyle: 'solid',
    display: 'flex',
    textAlign: 'center',
    borderTopWidth: 0,
  },
  dividerWithChildrenLine: {
    borderColor: 'currentColor',
    borderStyle: 'inherit',
    borderWidth: 0,
    alignSelf: 'center',
    borderTopWidth: 'thin',
    width: '100%',
  },
  dividerWithChildrenWrapper: {
    color: colors.grey500,
    display: 'inline-block',
    whiteSpace: 'nowrap',
    paddingLeft: `calc(${system.unit} * 1.2)`,
    paddingRight: `calc(${system.unit} * 1.2)`,
  },
})

export type DividerProps<RootComponent extends ElementType = 'hr', BoxElement extends HTMLElement = HTMLHRElement> = BoxProps<RootComponent, BoxElement> & {
  /** 覆蓋內部子樣式的 xStyles */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
}

/**
 * Divider 是分隔線元件，可選擇性地在中間插入文字內容。
 * 預設渲染為 hr 標籤，若包含 children 則自動切換為 div。
 */
export const Divider = <RootComponent extends ElementType = 'hr', BoxElement extends HTMLElement = HTMLHRElement>(props: DividerProps<RootComponent, BoxElement>) => {
  const { component: componentProp = 'hr', rootStyle, children, xStyles, ...rest } = props
  const hasChildren = !!children
  const component = hasChildren ? 'div' : componentProp
  const baseStyles = useXStyles(_baseStyles, xStyles)

  return (
    <Box
      component={component}
      rootStyle={[
        baseStyles.reset,
        baseStyles.root,
        hasChildren ? baseStyles.dividerWithChildren : null,
        rootStyle,
      ]}
      {...rest}
    >
      {hasChildren
        ? (
          <>
            <span {...stylex.props(baseStyles.dividerWithChildrenLine)} />
            <span {...stylex.props(baseStyles.dividerWithChildrenWrapper)}>{children}</span>
            <span {...stylex.props(baseStyles.dividerWithChildrenLine)} />
          </>
        )
        : null}
    </Box>
  )
}
